import React, { useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Images, ArrowLeft } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { journey } from '../data/journey';
export function GalleryPage() {
  const [lightbox, setLightbox] = useState<{
    src: string;
    caption: string;
  } | null>(null);
  const location = useLocation();
  // Scroll to the year anchor (e.g. #year-2022) when arriving from the Journey page
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        // Defer to ensure layout is ready
        requestAnimationFrame(() => el.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        }));
      }
    }
  }, [location.hash]);
  return <main className="bg-ivory text-midnight pt-24 pb-24" data-id="element-423">
      {/* Header */}
      <section className="bg-midnight text-ivory py-24 text-center relative overflow-hidden" data-id="element-424">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-midnight-light to-transparent opacity-50 pointer-events-none" data-id="element-425" />
        <div className="relative z-10 px-6" data-id="element-426">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-xs font-bold tracking-widest uppercase mb-6 text-gold" data-id="element-427">
            <Images className="w-4 h-4" data-id="element-428" />
            Gallery
          </div>
          <h1 className="text-5xl md:text-7xl font-display font-bold tracking-tighter mb-6" data-id="element-429">
            Faith in{' '}
            <span className="font-serif italic font-medium text-gold" data-id="element-430">
              Action
            </span>
          </h1>
          <p className="text-lg md:text-xl text-ivory/70 font-medium max-w-2xl mx-auto" data-id="element-431">
            Every moment, year by year — outreaches, vigils, and the lives God
            has touched through Camp David.
          </p>
          <div className="mt-10" data-id="element-432">
            <Link to="/journey" className="inline-flex items-center gap-2 border-2 border-white/15 px-6 py-3 rounded-full text-sm font-bold tracking-wide hover:border-gold hover:text-gold transition-colors" data-id="element-433">
              <ArrowLeft className="w-4 h-4" data-id="element-434" />
              Back to the journey
            </Link>
          </div>
        </div>
      </section>

      {/* Year sections */}
      <div className="max-w-7xl mx-auto px-6 mt-24 space-y-28" data-id="element-435">
        {journey.map(event => <section key={event.year} id={`year-${event.year}`} className="scroll-mt-28" data-id="element-436">
            <div className="flex items-end justify-between gap-6 mb-10 border-b-2 border-midnight/5 pb-6" data-id="element-437">
              <div data-id="element-438">
                <div className="inline-block bg-gold text-midnight px-3 py-1 rounded-full text-xs font-bold tracking-widest mb-3" data-id="element-439">
                  {event.year}
                </div>
                <h2 className="text-3xl md:text-5xl font-display font-bold tracking-tight leading-tight" data-id="element-440">
                  {event.title}
                </h2>
              </div>
              <span className="hidden md:block font-display font-black text-7xl text-midnight/5 leading-none select-none" data-id="element-441">
                {String(event.year).slice(-2)}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-id="element-442">
              {event.images.map((img, i) => <motion.button key={i} type="button" onClick={() => setLightbox(img)} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.4,
            delay: i * 0.05
          }} className={`group relative overflow-hidden rounded-[1.5rem] bg-midnight/5 shadow-sm hover:shadow-xl transition-all duration-500 aspect-square ${i % 2 === 0 ? 'rotate-1 hover:rotate-0' : '-rotate-1 hover:rotate-0'}`} data-id="element-443">
                  <img src={img.src} alt={img.caption} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out" data-id="element-444" />
                  <div className="absolute inset-0 bg-gradient-to-t from-midnight/80 via-midnight/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-445" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500" data-id="element-446">
                    <p className="text-ivory text-xs md:text-sm font-serif italic text-left" data-id="element-447">
                      {img.caption}
                    </p>
                  </div>
                </motion.button>)}
            </div>
          </section>)}
      </div>

      {/* Lightbox */}
      <AnimatePresence data-id="element-448">
        {lightbox && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] bg-midnight/95 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setLightbox(null)} role="dialog" aria-modal="true" data-id="element-449">
            <button onClick={() => setLightbox(null)} aria-label="Close" className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-ivory flex items-center justify-center transition-colors" data-id="element-450">
              <X className="w-6 h-6" data-id="element-451" />
            </button>
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="max-w-5xl w-full relative" onClick={e => e.stopPropagation()} data-id="element-452">
              <div className="bg-white p-4 rounded-[2rem] shadow-2xl rotate-1" data-id="element-453">
                <img src={lightbox.src} alt={lightbox.caption} className="w-full max-h-[75vh] object-contain rounded-2xl bg-midnight" data-id="element-454" />
                <p className="text-midnight text-center mt-6 mb-2 font-serif italic text-xl" data-id="element-455">
                  {lightbox.caption}
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </main>;
}